import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/ui/use-toast';
import { setViewerTimeZone } from '@/lib/trfTime';

/**
 * The signed-in person's timezone setting (`User.profile.timezone`), as a form:
 * the value being edited, whether it differs from what is saved, and a save.
 * Shared by the parent Settings page and staff Settings (#2741).
 *
 * '' is a real setting: "never chose one", so staff times follow the browser.
 * A saved choice applies at once (trfTime is told directly, then the session
 * user is re-read), not on the next reload.
 */
export function useTimezoneSetting() {
  const { t } = useTranslation('settings');
  const { toast } = useToast();
  const { user, refresh } = useAuth();
  const saved = user?.profile?.timezone || '';
  const [timezone, setTimezone] = useState(saved);
  const [saving, setSaving] = useState(false);

  // Follow the saved value when the session user is re-read.
  useEffect(() => {
    setTimezone(saved);
  }, [saved]);

  const save = async () => {
    setSaving(true);
    try {
      await api.auth.updateProfile({ timezone });
      setViewerTimeZone(timezone || null);
      try { await refresh(); } catch { /* the save stands; /me confirms the same zone on the next load */ }
      toast({ title: t('toasts.profileSaved') });
    } catch {
      toast({ variant: 'destructive', title: t('toasts.profileFailed'), description: t('toasts.notSavedDescription') });
    } finally {
      setSaving(false);
    }
  };

  return { timezone, setTimezone, saving, save, dirty: timezone !== saved };
}
